import cx from "clsx";
import React from "react";

import FormControlShell from "../FormControlShell";

import styles from "./index.module.scss";

const SIZE = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
  xl: styles.sizeXl,
};

type OwnProps = {
  className?: string;
  style?: React.CSSProperties;

  size?: keyof typeof SIZE;
  color?: React.ComponentProps<typeof FormControlShell>["color"];
  disabled?: boolean;
  label?: React.ReactNode;
  multiline?: number;
  onChange?: (value: string) => void;
  placeholder?: string;
  slotLeft?: React.ReactNode;
  leftAdornment?: React.ReactNode;
  slotRight?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  value: string;
  alwaysShrinkLabel?: boolean;
};

type ForwardedProps = React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement>;

type Props = OwnProps & Omit<ForwardedProps, keyof OwnProps>;

function Root({
  className,
  style,

  size = "md",
  color,
  disabled,
  label,
  multiline,
  onChange,
  placeholder,
  slotLeft,
  leftAdornment,
  slotRight,
  rightAdornment,
  value,
  alwaysShrinkLabel,

  ...others
}: Props) {
  const Component = multiline ? "textarea" : "input";
  const inputRef = React.useRef<HTMLTextAreaElement & HTMLInputElement>(null);

  return (
    <div
      className={cx(
        styles.container,
        className,
        SIZE[size],
        disabled ? styles.disabled : null
      )}
      style={style}
    >
      <FormControlShell
        label={label}
        isValueEmpty={!value}
        color={color}
        controlDescendantPlaceholder
        disabled={disabled}
        alwaysShrinkLabel={alwaysShrinkLabel}
      >
        <div
          className={styles.content}
          onClick={() => {
            inputRef.current?.focus();
          }}
        >
          {slotLeft ? <div className={styles.slot}>{slotLeft}</div> : null}
          <div className={styles.inputContainer}>
            {leftAdornment}
            <Component
              ref={inputRef}
              className={styles.input}
              value={value}
              onChange={(event) => onChange?.(event.target.value)}
              placeholder={placeholder}
              rows={multiline || undefined}
              disabled={disabled}
              {...others}
            />
            {rightAdornment}
          </div>
          {slotRight ? <div className={styles.slot}>{slotRight}</div> : null}
        </div>
      </FormControlShell>
    </div>
  );
}

function Adornment({
  className,
  style,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  return (
    <div className={cx(styles.inputAdornment, className)} style={style}>
      {children}
    </div>
  );
}

const InputV2 = Object.assign(Root, { Root, Adornment });

export default InputV2;
